import * as Yup from "yup";

import { FieldConfiguration, fieldConfigurations } from "./fieldConfigurations";

// A commission is meaningless without the agent it is paid to, so each of
// these rows requires its agent row to be mapped as well.
export const agentByCommissionField: Record<string, FieldConfiguration> = {
  [fieldConfigurations.STOCK_AGENT_PERCENTAGE_COMMISSION.value]:
    fieldConfigurations.STOCK_AGENT,
  [fieldConfigurations.REBATE_AGENT_PERCENTAGE_COMMISSION.value]:
    fieldConfigurations.REBATE_AGENT,
};

export interface BuildValidationSchemaOptions {
  rawData: Record<string, string | number>;
  businessIdByAgentCodeLookup: Record<string, string>;
  businessIdBySaleyardCodeLookup: Record<string, string>;
}

export const unmatchedBusinessMessage = (configuration: FieldConfiguration) =>
  `No business has this Agent Code or Saleyard Code. Add one, or map ${configuration.label} to a different column.`;

export const agentRequiredMessage = (
  configuration: FieldConfiguration,
  agentConfiguration: FieldConfiguration,
) =>
  `${agentConfiguration.label} is required when ${configuration.label} is present.`;

export const matchesExistingBusiness = (
  sourceColumn: string | null | undefined,
  {
    rawData,
    businessIdByAgentCodeLookup,
    businessIdBySaleyardCodeLookup,
  }: BuildValidationSchemaOptions,
): boolean => {
  const selectedValue = sourceColumn ? rawData[sourceColumn] : undefined;
  if (!selectedValue) {
    return true;
  }
  return Boolean(
    businessIdByAgentCodeLookup[selectedValue] ||
      businessIdBySaleyardCodeLookup[selectedValue],
  );
};

// Yup interpolates the field path into its built in messages, and the path
// here is the internal `Value__x` name. Cached, as labelling clones.
const labelledValidations = new WeakMap<FieldConfiguration, Yup.AnySchema>();

export const getLabelledValidation = (
  configuration: FieldConfiguration,
): Yup.AnySchema => {
  let schema = labelledValidations.get(configuration);
  if (!schema) {
    schema = (configuration.validation || Yup.string()).label(
      configuration.label,
    );
    labelledValidations.set(configuration, schema);
  }
  return schema;
};

export function buildSideLoadedValidationSchema(
  configurations: FieldConfiguration[],
  options: BuildValidationSchemaOptions,
): Yup.AnyObjectSchema {
  const shape: Record<string, Yup.AnySchema> = {};
  configurations.forEach(configuration => {
    shape[`MappableField__${configuration.value}`] = Yup.string().nullable();
    shape[`Value__${configuration.value}`] = Yup.string().when(
      `MappableField__${configuration.value}`,
      (mappableFieldValue, schema) => {
        const agentConfiguration = agentByCommissionField[configuration.value];
        if (configuration.isBusinessLookup) {
          return Yup.string()
            .test({
              name: "matchesExistingBusiness",
              message: unmatchedBusinessMessage(configuration),
              test: () => matchesExistingBusiness(mappableFieldValue, options),
            })
            .label(configuration.label);
        }
        if (agentConfiguration) {
          return Yup.string()
            .test({
              name: "agentRequired",
              message: agentRequiredMessage(configuration, agentConfiguration),
              test() {
                // `this` is the Yup test context; access sibling fields via this.parent
                return (
                  !mappableFieldValue ||
                  Boolean(
                    this.parent[`MappableField__${agentConfiguration.value}`],
                  )
                );
              },
            })
            .label(configuration.label);
        }
        if (mappableFieldValue !== null) {
          return getLabelledValidation(configuration);
        }
        // A label on the outer schema is discarded once `when` resolves, so it
        // has to be applied to whatever this returns.
        return schema.label(configuration.label);
      },
    );
  });
  return Yup.object().shape(shape);
}
